import * as React from 'react';

import { Close } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot as SlotPrimitive } from 'radix-ui';

import { typographyVariants } from './typography.jsx';

const chipVariants = cva(
  'box-border flex w-fit items-center gap-1 rounded-full select-none aria-disabled:pointer-events-none aria-disabled:opacity-[0.32] data-[size=large]:h-8 data-[size=large]:px-3 data-[size=large]:py-2 data-[size=medium]:h-6 data-[size=medium]:px-2 data-[size=medium]:py-1 data-[size=small]:h-5 data-[size=small]:px-1 data-[size=small]:py-0.5',
  {
    variants: {
      variant: {
        filled: '',
        outline: 'border',
      },

      color: {
        primary:
          'bg-primary-light fill-primary-contrastText text-primary-contrastText hover:bg-primary-chip-shade',
        secondary:
          'bg-secondary-light fill-secondary-contrastText text-secondary-contrastText hover:bg-secondary-chip-shade',
        tertiary:
          'bg-tertiary-light fill-tertiary-contrastText text-tertiary-contrastText hover:bg-tertiary-chip-shade',
        success:
          'bg-success-light fill-success-contrastText text-success-contrastText',
        error: 'bg-error-light fill-error-contrastText text-error-contrastText',
        warning:
          'bg-warning-light fill-warning-contrastText text-warning-contrastText',
        info: 'bg-info-light fill-info-contrastText text-info-contrastText',
        silver:
          'bg-background-silver fill-silver-contrastText text-silver-contrastText',
        transparent: 'bg-transparent fill-text-primary text-text-primary',
        teal: 'bg-background-teal fill-teal-contrastText text-teal-contrastText',
        purple:
          'bg-background-purple fill-purple-contrastText text-purple-contrastText',
        yellow:
          'bg-background-yellow fill-yellow-contrastText text-yellow-contrastText',
        fuchsia:
          'bg-background-fuchsia fill-fuchsia-contrastText text-fuchsia-contrastText',
        royal:
          'bg-background-royal fill-royal-contrastText text-royal-contrastText',
        mint: 'bg-background-mint fill-mint-contrastText text-mint-contrastText',
        azure:
          'bg-background-azure fill-azure-contrastText text-azure-contrastText',
        blaze:
          'bg-background-blaze fill-blaze-contrastText text-blaze-contrastText',
      },
    },
    compoundVariants: [
      {
        variant: 'outline',
        color: 'primary',
        class:
          'border-primary-main bg-transparent-main fill-primary-dark text-primary-dark hover:bg-background-primary',
      },
      {
        variant: 'outline',
        color: 'secondary',
        class:
          'border-secondary-main bg-transparent-main fill-secondary-dark text-secondary-dark hover:bg-background-secondary',
      },
      {
        variant: 'outline',
        color: 'tertiary',
        class:
          'border-tertiary-main bg-transparent-main fill-tertiary-dark text-tertiary-dark hover:bg-background-tertiary',
      },
      {
        variant: 'outline',
        color: 'success',
        class:
          'border-success-main bg-transparent-main fill-success-dark text-success-dark',
      },
      {
        variant: 'outline',
        color: 'error',
        class:
          'border-error-main bg-transparent-main fill-error-dark text-error-dark',
      },
      {
        variant: 'outline',
        color: 'warning',
        class:
          'border-warning-main bg-transparent-main fill-warning-dark text-warning-dark',
      },
      {
        variant: 'outline',
        color: 'info',
        class:
          'border-info-main bg-transparent-main fill-info-dark text-info-dark',
      },
      {
        variant: 'outline',
        color: 'silver',
        class:
          'border-silver-main bg-transparent-main fill-silver-dark text-silver-dark',
      },
      {
        variant: 'outline',
        color: 'teal',
        class:
          'border-teal-main bg-transparent-main fill-teal-dark text-teal-dark',
      },
      {
        variant: 'outline',
        color: 'purple',
        class:
          'border-purple-main bg-transparent-main fill-purple-dark text-purple-dark',
      },
      {
        variant: 'outline',
        color: 'yellow',
        class:
          'border-yellow-main bg-transparent-main fill-yellow-dark text-yellow-dark',
      },
      {
        variant: 'outline',
        color: 'fuchsia',
        class:
          'border-fuchsia-main bg-transparent-main fill-fuchsia-dark text-fuchsia-dark',
      },
      {
        variant: 'outline',
        color: 'royal',
        class:
          'border-royal-main bg-transparent-main fill-royal-dark text-royal-dark',
      },
      {
        variant: 'outline',
        color: 'mint',
        class:
          'border-mint-main bg-transparent-main fill-mint-dark text-mint-dark',
      },
      {
        variant: 'outline',
        color: 'azure',
        class:
          'border-azure-main bg-transparent-main fill-azure-dark text-azure-dark',
      },
      {
        variant: 'outline',
        color: 'blaze',
        class:
          'border-blaze-main bg-transparent-main fill-blaze-dark text-blaze-dark',
      },
    ],
    defaultVariants: {
      variant: 'filled',
      color: 'tertiary',
    },
  },
);

type ChipProps = React.ComponentProps<'span'> &
  VariantProps<typeof chipVariants> & {
    asChild?: boolean;
    deletable?: boolean;
    disabled?: boolean;
    onDelete?: () => void;
    size?: 'small' | 'medium' | 'large';
  };

function Chip({
  className,
  variant,
  color,
  size = 'medium',
  deletable = false,
  asChild = false,
  disabled = false,
  onDelete,
  ...props
}: ChipProps) {
  const Comp = asChild ? SlotPrimitive.Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      aria-disabled={disabled}
      data-size={size}
      className={cn(
        chipVariants({ variant, color }),
        typographyVariants({
          variant: size === 'large' ? 'bodyShort02' : 'caption1',
        }),

        className,
      )}
      {...props}
    >
      {props.children}
      {deletable && (
        <button
          type="button"
          className="size-4 cursor-pointer opacity-70 hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.();
          }}
        >
          <Close className="size-4" />
        </button>
      )}
    </Comp>
  );
}

export { Chip, type ChipProps };
